// src/auth/AuthProvider.tsx (수정된 코드)
import React, { createContext, useState, useContext, useEffect, useCallback } from "react";
import axios, { AxiosError } from 'axios';
import { useNavigate } from "react-router-dom";
// jwtDecode는 토큰 복원 시 유효성 검사 목적 외에는 더 이상 사용하지 않습니다.

type User = { id: string; name: string; role: string } | null;

interface AuthContextType {
  user: User;
  login: (accessToken: string, refreshToken: string, userInfo: User) => void;
  logout: () => void;
  accessToken: string | null;
  refreshToken: string | null;
  loading: boolean;
  // *추가: 전역적으로 사용하기 위해 Axios 인스턴스를 제공하는 것이 좋습니다.
  // 이 예시에서는 global axios를 설정하는 것으로 대체합니다.
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
// const SOCKET_URL = 'http://172.16.20.112:3000'; // 로컬 서버 주소
const SOCKET_URL = "https://rek-hybrid.co.kr"; // socket.io 서버

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // 토큰 갱신 중 동시 요청 방지 플래그 (선택 사항이지만 강력 권장)
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [requestsQueue, setRequestsQueue] = useState<any[]>([]);
  
  // ✅ useCallback을 사용하여 안정적인 함수 보장
  const logout = useCallback(() => {
    console.log(`[Client LOG] 🔴 LOGOUT: 모든 토큰 (AT/RT) 소멸 및 localStorage 클리어.`);
    localStorage.clear();
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    // ✅ 토큰이 사라졌으니, Axios 기본 헤더도 제거
    delete axios.defaults.headers.common['Authorization']; 
    navigate("/login");
  }, [navigate]); // navigate에 의존성 추가

  const login = (access: string, refresh: string, userInfo: User) => {
    localStorage.setItem("accessToken", access);
    localStorage.setItem("refreshToken", refresh);
    localStorage.setItem("user", JSON.stringify(userInfo));

    setUser(userInfo);
    setAccessToken(access);
    setRefreshToken(refresh);

    //console.log("액세스 토큰 발행 : ",access);
    
    // ✅ 로그인 시 Axios 기본 헤더에 Access Token 설정
    axios.defaults.headers.common['Authorization'] = `Bearer ${access}`;
  };

  // 1. 새로고침 시 localStorage에서 상태 복원 (단순화)
  useEffect(() => {
    const access = localStorage.getItem("accessToken");
    const refresh = localStorage.getItem("refreshToken");
    const storedUser = localStorage.getItem("user");

    if (access && refresh && storedUser) {
        // ✅ 토큰이 존재하면 일단 상태 복원. 만료 여부는 Interceptor가 담당.
        setUser(JSON.parse(storedUser));
        setAccessToken(access);
        setRefreshToken(refresh);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access}`;
        //console.log("리프레시 토큰 발행 : ",refresh);
    }
    setLoading(false);
  }, []); // 초기 1회만 실행

  // 2. Axios Interceptor 설정 (가장 중요)
  useEffect(() => {
    
    // 이 부분에서 `accessToken`, `refreshToken`, `logout` 함수 등을 사용합니다.
    const interceptor = axios.interceptors.response.use(
        (response) => response, // 200번대 응답은 그대로 통과
        async (error: AxiosError) => {
            const originalRequest = error.config;
            
            // 401 에러이고, 토큰 갱신 재시도 요청이 아닌 경우에만 처리
            if (error.response?.status === 401 && originalRequest && !(originalRequest as any)._retry) {
                console.log(`[Client LOG] ⚠️ 401 에러 감지: Access Token 만료 추정. 갱신 시도.`);
                (originalRequest as any)._retry = true; // 재시도 플래그 설정
                
                // Refresh Token이 없으면 로그아웃 처리
                const currentRefreshToken = localStorage.getItem("refreshToken");
                if (!currentRefreshToken) {
                    console.log(`[Client LOG] 🚫 RT 없음: 로그아웃 처리.`);
                    logout();
                    return Promise.reject(error);
                }

                try {
                    // Refresh API 호출
                    const response = await axios.post(`${SOCKET_URL}/api/auth/refresh`, { 
                        refreshToken: currentRefreshToken 
                    });
                    
                    const newAccess = response.data.accessToken;
                    
                    // 1. 상태 및 저장소 업데이트
                    console.log(`[Client LOG] ✅ AT 갱신 성공: 새 AT 저장 및 요청 재시도.`);
                    localStorage.setItem("accessToken", newAccess);
                    setAccessToken(newAccess); // 상태 업데이트
                    
                    // 2. 실패했던 원래 요청의 헤더 업데이트
                    originalRequest.headers['Authorization'] = `Bearer ${newAccess}`;
                    axios.defaults.headers.common['Authorization'] = `Bearer ${newAccess}`;
                    //console.log("액세스 토큰 재발행 : ",newAccess);

                    // 3. 실패했던 원래 요청 재시도
                    return axios(originalRequest);

                } catch (refreshError) {
                    // Refresh API 호출 실패 (Refresh Token 만료 또는 유효하지 않음)
                    console.error("Refresh Token 갱신 실패:", refreshError);
                    logout(); // 강제 로그아웃
                    return Promise.reject(refreshError);
                }
            }

            return Promise.reject(error);
        }
    );

    // 컴포넌트 언마운트 시 Interceptor 제거 (클린업)
    return () => {
        axios.interceptors.response.eject(interceptor);
    };
  }, [logout]); // logout 함수에 의존성 추가

  if (loading) {
    return <div>로딩 중...</div>; // 로딩 중 화면 표시
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, accessToken, refreshToken, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook은 기존과 동일
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};